import React from 'react';

/**
 * Pure SVG ternary triangle. Coordinates come from `src/utils/ternary.js`
 * where the math-space y-axis points up. We flip to screen-space here.
 *
 * Colors are driven by CSS classes (defaults in src/styles/e3d-theme.css)
 * so the visual theme toggles cleanly between default and 'e3d'.
 *
 * Props:
 *   - points: [{ label, x, y, n? }, ...] — each participant's position
 *   - centroid: { x, y } | null  — group average to render as a ring
 *   - highlightLabels: [labels]  — drawn larger and in accent colour
 *   - meLabel: label             — the current viewer's own point
 *   - axesLabels: { innocence, honneur, puissance } — vertex captions
 *   - size: pixel width          — SVG keeps aspect; height derived
 */

const SQRT3_2 = Math.sqrt(3) / 2;
const SCREEN_H = SQRT3_2;
const PAD = 0.18;
const VB_X = -PAD;
const VB_Y = -PAD;
const VB_W = 1 + 2 * PAD;
const VB_H = SCREEN_H + 2 * PAD;

function toScreen(p) {
  return { x: p.x, y: SCREEN_H - p.y };
}

const V = {
  innocence: toScreen({ x: 0, y: 0 }),
  honneur:   toScreen({ x: 1, y: 0 }),
  puissance: toScreen({ x: 0.5, y: SQRT3_2 })
};

const DEFAULT_LABELS = {
  innocence: 'Innocence',
  honneur: 'Honneur',
  puissance: 'Puissance'
};

export default function TernaryTriangle({
  points = [],
  centroid = null,
  highlightLabels = [],
  meLabel = null,
  axesLabels = DEFAULT_LABELS,
  size = 380
}) {
  const aspectH = size * (VB_H / VB_W);
  const trianglePath = `M ${V.innocence.x} ${V.innocence.y} L ${V.honneur.x} ${V.honneur.y} L ${V.puissance.x} ${V.puissance.y} Z`;
  const highlightSet = new Set(highlightLabels || []);

  // Important points last so they sit on top of the crowd.
  const ordered = [...points].sort((a, b) => {
    const score = (p) => (p.label === meLabel ? 2 : highlightSet.has(p.label) ? 1 : 0);
    return score(a) - score(b);
  });

  return (
    <svg
      viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
      width={size}
      height={aspectH}
      style={{ display: 'block', maxWidth: '100%', height: 'auto', overflow: 'visible' }}
      role="img"
      aria-label="Triangle ternaire des positions du groupe"
    >
      {/* Background triangle */}
      <path className="tt-triangle" d={trianglePath} />

      {/* Internal guide lines from each vertex to the opposite midpoint */}
      <g className="tt-guide" strokeDasharray="0.012,0.008">
        <line
          x1={V.innocence.x} y1={V.innocence.y}
          x2={(V.honneur.x + V.puissance.x) / 2} y2={(V.honneur.y + V.puissance.y) / 2}
        />
        <line
          x1={V.honneur.x} y1={V.honneur.y}
          x2={(V.innocence.x + V.puissance.x) / 2} y2={(V.innocence.y + V.puissance.y) / 2}
        />
        <line
          x1={V.puissance.x} y1={V.puissance.y}
          x2={(V.innocence.x + V.honneur.x) / 2} y2={(V.innocence.y + V.honneur.y) / 2}
        />
      </g>

      {/* Vertex labels */}
      <g fontFamily="inherit">
        <text
          className="tt-label tt-label--innocence"
          x={V.innocence.x - 0.02} y={V.innocence.y + 0.08}
          fontSize="0.055" textAnchor="start"
        >
          {axesLabels.innocence}
        </text>
        <text
          className="tt-label tt-label--honneur"
          x={V.honneur.x + 0.02} y={V.honneur.y + 0.08}
          fontSize="0.055" textAnchor="end"
        >
          {axesLabels.honneur}
        </text>
        <text
          className="tt-label tt-label--puissance"
          x={V.puissance.x} y={V.puissance.y - 0.05}
          fontSize="0.055" textAnchor="middle"
        >
          {axesLabels.puissance}
        </text>
      </g>

      {/* Centroid (group average) — drawn underneath points */}
      {centroid && (() => {
        const c = toScreen(centroid);
        return (
          <g>
            <circle
              cx={c.x} cy={c.y} r="0.035"
              style={{
                fill: 'none',
                stroke: 'var(--centroid-stroke, #27ae60)',
                strokeWidth: 0.006
              }}
            />
            <circle
              cx={c.x} cy={c.y} r="0.008"
              style={{ fill: 'var(--centroid-stroke, #27ae60)' }}
            />
          </g>
        );
      })()}

      {/* Participant points */}
      {ordered.map((p) => {
        if (p == null || p.x == null || p.y == null) return null;
        const s = toScreen(p);
        const isMe = p.label === meLabel;
        const isHl = highlightSet.has(p.label);
        const r = isMe ? 0.028 : isHl ? 0.024 : 0.016;
        const fillVar = isMe
          ? 'var(--me-fill, #e74c3c)'
          : isHl
          ? 'var(--highlight-fill, #f39c12)'
          : 'var(--participant-fill, #3498db)';
        return (
          <g key={p.label}>
            <circle
              cx={s.x} cy={s.y} r={r}
              style={{
                fill: fillVar,
                stroke: isMe || isHl ? 'var(--color-bg, #fff)' : 'none',
                strokeWidth: 0.005,
                opacity: isMe || isHl ? 1 : 0.78
              }}
            />
            {(isMe || isHl) && (
              <text
                x={s.x}
                y={s.y - r - 0.012}
                fontSize="0.035"
                textAnchor="middle"
                style={{ fill: fillVar, fontWeight: 600 }}
              >
                {isMe ? 'Vous' : p.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
